<template>
  <div v-if="show" class="job-open-question">
    <template v-for="(container, index) in questionContainerList">
      <div v-if="!container.hidden"  class="open-question-container" :key="index">
        <div class="container-title">{{ container.title }}</div>
        <div class="container-tip">
          <div v-for="(item, index) in container.tipMessage" :key="index">{{ item }}</div>
        </div>
        <QuestionListEditor
          v-model="stepData[container.dataKey]"
          :disabled="isNotEditableJobTemplate || disabled"
          :has-visibility-option="container.hasVisibilityOption"
          :group-list="groupList"
          :disabled-question-type="container.disabledQuestionType"
          :default-visibility="defaultVisibilityType"
          :size-limit="container.questionSizeLimit"
          :disable-question-group="disableQuestionGroup"
        />
      </div>
    </template>
    <template v-for="container in questionnaireContainerList">
      <div v-if="!container.hidden" class="open-question-container questionnaire-container" :key="container.type">
        <div class="container-title">{{ container.title }}</div>
        <div class="container-tip">
          <div v-for="(item, index) in container.tipMessage" :key="index">{{ item }}</div>
        </div>
        <el-button
          v-if="!getSelectedQuestionnaire(container.type)"
          type="text"
          class="select-questionnaire-btn"
          :disabled="isNotEditableJobTemplate || disabled"
          @click="handleOpenQuestionnaireSelector(container.type)"
        >
          {{ $t(`${i18nPrefix}.btn.selectQuestionnaire`) }}
        </el-button>
        <div v-else class="selected-questionnaire-panel">
          <div class="selected-questionnaire-header">
            <div class="selected-questionnaire-info">
              <div class="selected-questionnaire-name">{{ getQuestionnaireName(getSelectedQuestionnaire(container.type)) }}</div>
              <div class="selected-questionnaire-scale">{{ getQuestionnaireScaleText(getSelectedQuestionnaire(container.type)) }}</div>
            </div>
            <div v-if="!isNotEditableJobTemplate && !disabled" class="selected-questionnaire-actions">
              <div class="questionnaire-action-btn" @click="handleOpenQuestionnaireSelector(container.type)">
                <i class="el-icon-edit-outline"></i>
              </div>
              <div class="questionnaire-action-btn" @click="handleClearQuestionnaire(container.type)">
                <i class="iconfont iconDelete"></i>
              </div>
            </div>
          </div>
          <QuestionnairePreview
            v-if="getSelectedQuestionnaireItems(container.type).length > 0"
            class="job-questionnaire-preview"
            :showQuestionLabel="false"
            :questionnaire="getSelectedQuestionnaire(container.type)"
            :items="getSelectedQuestionnaireItems(container.type)"
          />
        </div>
      </div>
    </template>
    <QuestionnaireSelectorDialog
      ref="questionnaireSelectorDialog"
      :show.sync="questionnaireSelector.show"
      :selectedQuestionnaire="activeSelectedQuestionnaire"
      :usageType="questionnaireSelector.type"
      :companyId="questionnaireCompanyId"
      :pageSize="200"
      @confirm="handleConfirmQuestionnaire"
    />
  </div>
</template>

<script>
import mixin from './mixin'
import { QUESTION_TYPE, MAX_NORMAL_QUESTION_VIDEO_TYPE_SIZE_LIMIT, MAX_NORMAL_QUESTION_ATTACHMENT_TYPE_SIZE_LIMIT } from '@/constant/openQuestion'
import QuestionListEditor from '@/components/openQuestion/questionListEditor.vue'
import { mapActions } from 'vuex'
import _ from 'lodash'
import QuestionnaireSelectorDialog from '@/views/adminNew/applicantList/dialog/components/questionnaireSelectorDialog.vue'
import QuestionnairePreview from '@/views/adminNew/applicantList/dialog/components/questionnairePreview.vue'

const QUESTION_CONTAINER_TYPE = {
  PRESCREEN: 'prescreen',
  NORMAL: 'normal',
  HR: 'hr'
}

const QUESTIONNAIRE_USAGE_TYPE = {
  INTERVIEW: 'interview',
  RATING: 'rating'
}

const QUESTIONNAIRE_FIELD_MAP = {
  [QUESTIONNAIRE_USAGE_TYPE.INTERVIEW]: 'defaultInterviewQuestionnaireId',
  [QUESTIONNAIRE_USAGE_TYPE.RATING]: 'defaultRatingQuestionnaireId'
}

export default {
  mixins: [mixin],
  components: {
    QuestionListEditor,
    QuestionnaireSelectorDialog,
    QuestionnairePreview
  },
  data () {
    return {
      hasForm: false,
      QUESTION_CONTAINER_TYPE,
      QUESTIONNAIRE_USAGE_TYPE,
      groupListLoader: null,
      groupList: [],
      selectedQuestionnaireMap: {
        [QUESTIONNAIRE_USAGE_TYPE.INTERVIEW]: null,
        [QUESTIONNAIRE_USAGE_TYPE.RATING]: null
      },
      questionnaireSelector: {
        show: false,
        type: null
      },
      stepData: {
        prescreen: [],
        normal: [],
        hr: [],
        defaultInterviewQuestionnaireId: null,
        defaultRatingQuestionnaireId: null
      }
    }
  },
  computed: {
    disableQuestionGroup () {
      return !this.hasOpenQuestionTemplatePermission
    },
    defaultVisibilityType () {
      return this.jobInfo.job_visibility
    },
    questionnaireCompanyId () {
      return this.companyDataParams?.company_id || this.jobInfo.company?.id || null
    },
    activeSelectedQuestionnaire () {
      return this.getSelectedQuestionnaire(this.questionnaireSelector.type)
    },
    showHmRatingQuestionnaire () {
      return !this.isTemplate && this.hasHMRatingPermission
    },
    showHmInterviewQuestionnaire () {
      return !this.isTemplate && this.hasHMInterviewPermission
    },
    questionContainerList () {
      return _.map(QUESTION_CONTAINER_TYPE, (type, typeName) => {
        const disabledQuestionType = []
        const questionSizeLimit = {}
        let hidden = false
        let hasVisibilityOption = false
        if (type === QUESTION_CONTAINER_TYPE.PRESCREEN) {
          hidden = !this.hasPrescreenSurveyPermission
          disabledQuestionType.push(QUESTION_TYPE.TEXT, QUESTION_TYPE.CHECKBOX, QUESTION_TYPE.VIDEO, QUESTION_TYPE.ATTACHMENT, QUESTION_TYPE.DATE)
        } else if (type === QUESTION_CONTAINER_TYPE.NORMAL) {
          hasVisibilityOption = this.allowEmployeeApplyJob
          questionSizeLimit[QUESTION_TYPE.VIDEO] = MAX_NORMAL_QUESTION_VIDEO_TYPE_SIZE_LIMIT
          questionSizeLimit[QUESTION_TYPE.ATTACHMENT] = MAX_NORMAL_QUESTION_ATTACHMENT_TYPE_SIZE_LIMIT
        }
        if (type !== QUESTION_CONTAINER_TYPE.HR) {
          disabledQuestionType.push(QUESTION_TYPE.SINGLE_SELECT, QUESTION_TYPE.MULTIPLE_SELECT)
        }
        if (!this.hasVideoQuestionPermission) {
          disabledQuestionType.push(QUESTION_TYPE.VIDEO)
        }
        if (!this.hasAttachmentQuestionPermission) {
          disabledQuestionType.push(QUESTION_TYPE.ATTACHMENT)
        }
        return {
          hidden,
          dataKey: type,
          title: this.$t(`${this.i18nPrefix}.openQuestionContainerType.${typeName}`),
          tipMessage: this.$t(`${this.i18nPrefix}.message.openQuestionContainerTip.${typeName}`),
          hasVisibilityOption,
          disabledQuestionType,
          questionSizeLimit
        }
      })
    },
    questionnaireContainerList () {
      return _.map(QUESTIONNAIRE_USAGE_TYPE, (type, typeName) => {
        let hidden = false
        if (type === QUESTIONNAIRE_USAGE_TYPE.RATING) {
          hidden = !this.showHmRatingQuestionnaire
        } else if (type === QUESTIONNAIRE_USAGE_TYPE.INTERVIEW) {
          hidden = !this.showHmInterviewQuestionnaire
        }
        return {
          hidden,
          type,
          title: this.$t(`${this.i18nPrefix}.openQuestionContainerType.${typeName}`),
          tipMessage: this.$t(`${this.i18nPrefix}.message.openQuestionContainerTip.${typeName}`)
        }
      })
    }
  },
  created () {
    this.loadGroupList()
  },
  methods: {
    ...mapActions('openQuestion', ['getQuestionTemplateGroupList']),
    async loadGroupList () {
      if (this.disableQuestionGroup) {
        return this.groupList
      }
      if (!this.groupListLoader) {
        this.groupListLoader = this.getQuestionTemplateGroupList({ active_only: 1, ...(this.companyDataParams || {}) }).then(resp => {
          if (resp) {
            this.groupList = resp
          }
          return this.groupList
        })
      }
      await this.groupListLoader
      return this.groupList
    },
    resetQuestionOrder (question, index) {
      question.order = index + 1
      return question
    },
    normalizeQuestionnaireId (value) {
      const numberValue = Number(value)
      return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null
    },
    getQuestionnaireFieldByType (type) {
      return QUESTIONNAIRE_FIELD_MAP[type]
    },
    getSelectedQuestionnaire (type) {
      return this.selectedQuestionnaireMap[type] || null
    },
    getSelectedQuestionnaireItems (type) {
      return _.get(this.getSelectedQuestionnaire(type), 'items') || []
    },
    getQuestionnaireName (questionnaire) {
      return questionnaire?.name || ''
    },
    getQuestionnaireScaleText (questionnaire) {
      const scaleScore = questionnaire?.scaleScore
      return scaleScore ? this.$t(`${this.i18nPrefix}.label.scaleWithValue`, { scale: scaleScore }) : this.$t(`${this.i18nPrefix}.label.noScale`)
    },
    async loadSelectedQuestionnaire (type, questionnaireId) {
      const id = this.normalizeQuestionnaireId(questionnaireId)
      if (!id) {
        this.$set(this.selectedQuestionnaireMap, type, null)
        return
      }
      await this.$nextTick()
      try {
        const questionnaire = await this.$refs.questionnaireSelectorDialog.loadQuestionnaireById(id)
        this.$set(this.selectedQuestionnaireMap, type, questionnaire)
      } catch (e) {
        this.$set(this.selectedQuestionnaireMap, type, {
          id,
          name: `${id}`,
          scaleScore: null,
          items: []
        })
      }
    },
    handleOpenQuestionnaireSelector (type) {
      if (this.isNotEditableJobTemplate || this.disabled) {
        return
      }
      this.questionnaireSelector = {
        show: true,
        type
      }
    },
    handleClearQuestionnaire (type) {
      const fieldName = this.getQuestionnaireFieldByType(type)
      this.$set(this.stepData, fieldName, null)
      this.$set(this.selectedQuestionnaireMap, type, null)
    },
    handleConfirmQuestionnaire (questionnaire) {
      const type = this.questionnaireSelector.type
      if (!type) {
        return
      }
      const fieldName = this.getQuestionnaireFieldByType(type)
      this.$set(this.stepData, fieldName, questionnaire?.id || null)
      this.$set(this.selectedQuestionnaireMap, type, questionnaire || null)
    },
    setStepDataToJobInfo () {
      const stepData = this.stepData
      const jobInfo = this.jobInfo
      this.$set(jobInfo, 'prescreen_questions', _.map(stepData.prescreen, this.resetQuestionOrder))
      this.$set(jobInfo, 'questions', _.map(stepData.normal, this.resetQuestionOrder))
      this.$set(jobInfo, 'hr_questions', _.map(stepData.hr, this.resetQuestionOrder))
      this.$set(jobInfo, 'default_interview_questionnaire_id', this.normalizeQuestionnaireId(stepData.defaultInterviewQuestionnaireId))
      this.$set(jobInfo, 'default_rating_questionnaire_id', this.normalizeQuestionnaireId(stepData.defaultRatingQuestionnaireId))
    },
    async getStepData () {
      const jobInfo = this.jobInfo
      const stepData = {
        prescreen: jobInfo.prescreen_questions || [],
        normal: jobInfo.questions || [],
        hr: jobInfo.hr_questions || [],
        defaultInterviewQuestionnaireId: this.normalizeQuestionnaireId(jobInfo.default_interview_questionnaire_id),
        defaultRatingQuestionnaireId: this.normalizeQuestionnaireId(jobInfo.default_rating_questionnaire_id)
      }
      const promiseList = []
      if (this.showHmRatingQuestionnaire) {
        promiseList.push(this.loadSelectedQuestionnaire(QUESTIONNAIRE_USAGE_TYPE.INTERVIEW, stepData.defaultInterviewQuestionnaireId))
      }
      if (this.showHmInterviewQuestionnaire) {
        promiseList.push(this.loadSelectedQuestionnaire(QUESTIONNAIRE_USAGE_TYPE.RATING, stepData.defaultRatingQuestionnaireId))
      }
      await Promise.all(promiseList)
      return stepData
    }
  }
}
</script>

<style lang="scss" scoped>
.job-open-question {
  .open-question-container {
    margin-bottom: 40px;
    padding: 20px;
    border: 1px dashed #8a8a8a;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }

    .container-title {
      background: #f7f7f7;
      font-size: 16px;
      font-weight: bold;
      transform: translateY(-50%);
      position: absolute;
      top: 0px;
      left: 10px;
    }

    .container-tip {
      margin-bottom: 10px;
    }

    .select-questionnaire-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 32px;
      margin-top: 20px;
      padding: 0 1px;
      border: 1px dashed #8a8a8a;
      border-radius: 0;
      background: transparent;
      box-sizing: border-box;
      font-size: 12px;
      line-height: 12px;
      cursor: pointer;

      &:disabled {
        border-color: #dcdfe6;
        background: #f5f7fa;
        color: #c0c4cc;
        cursor: not-allowed;
      }
    }

    .selected-questionnaire-panel {
      margin-top: 20px;

      .selected-questionnaire-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        min-height: 48px;
        padding: 5px 10px 5px 15px;
        background: #f1f1f1;
        box-sizing: border-box;

        .selected-questionnaire-info {
          min-width: 0;
        }

        .selected-questionnaire-name {
          overflow: hidden;
          color: #606266;
          font-size: 14px;
          line-height: 18px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .selected-questionnaire-scale {
          margin-top: 4px;
          color: #0096cc;
          font-size: 12px;
          line-height: 14px;
        }

        .selected-questionnaire-actions {
          display: flex;
          flex: 0 0 auto;
          align-items: center;
          gap: 10px;
          margin-left: 20px;
        }

        .questionnaire-action-btn {
          padding: 0;
          border: 0;
          background: transparent;
          color: #0096cc;
          font-size: 18px;
          line-height: 18px;
          cursor: pointer;

          .iconfont {
            font-size: 18px;
          }
        }
      }

      ::v-deep {
        .job-questionnaire-preview.questionnaire-preview.invitation-preview {
          display: block;

          .preview-question {
            display: block;
            margin-top: 10px;

            .preview-order {
              display: none;
            }

            .preview-card {
              min-height: 30px;
              padding: 5px 10px 5px 15px;
              border-bottom: 0;
              background: #f1f1f1;

              .preview-main {
                display: block;
                padding-bottom: 0;
                border-bottom: 0;

                .preview-title {
                  color: #606266;
                  font-size: 14px;
                  line-height: 18px;
                }

                .question-type-pill {
                  display: none;
                }
              }

              .question-weighting {
                margin-top: 4px;
                color: #0096cc;
                font-size: 12px;
                line-height: 14px;
              }

              .question-options {
                padding: 6px 0 0;

                .option-list {
                  gap: 4px;

                  .preview-option {
                    align-items: flex-start;
                    gap: 0;

                    .option-control {
                      display: none;
                    }

                    .option-text {
                      color: #606266;
                      font-size: 14px;
                      line-height: 22px;

                      &::before {
                        content: '-';
                        margin-right: 8px;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
